import type {
  RiskFactor,
  RiskFactorLevel,
  RiskFactors,
} from "@/core/compliance/entities/RiskFactors";

export interface RiskFactorDto {
  name?: string | null;
  code?: string | null;
  label?: string | null;
  level?: string | null;
  levelLabel?: string | null;
  points?: number | null;
  decisive?: boolean | null;
  applicable?: boolean | null;
  unknownCode?: boolean | null;
}

export interface RiskFactorsDto {
  businessKey?: string | null;
  scoringEventId?: string | null;
  scoredAt?: string | null;
  scoringVersion?: string | null;
  classification?: string | null;
  score?: number | null;
  scoreMax?: number | null;
  assessedEntityCount?: number | null;
  completenessPct?: number | null;
  hardOverrides?: string[] | null;
  summary?: string | null;
  decisiveFactors?: RiskFactorDto[] | null;
  factors?: RiskFactorDto[] | null;
}

const LEVELS: readonly RiskFactorLevel[] = ["RF", "RM", "RE"];

/**
 * Un niveau inconnu est ramene a `null`, jamais a l'un des trois niveaux connus : inventer un
 * niveau ferait lire « risque faible » sur une valeur que personne n'a evaluee.
 */
function mapLevel(raw: string | null | undefined): RiskFactorLevel | null {
  const found = LEVELS.find((level) => level === raw);
  return found ?? null;
}

function mapFactor(dto: RiskFactorDto): RiskFactor {
  return {
    name: dto.name ?? "",
    code: dto.code ?? null,
    label: dto.label ?? null,
    level: mapLevel(dto.level),
    levelLabel: dto.levelLabel ?? null,
    points: dto.points ?? 0,
    decisive: dto.decisive === true,
    // Un facteur dont l'applicabilite n'est pas dite est considere applicable : le masquer
    // reviendrait a cacher a l'agent un element que le moteur a bel et bien pris en compte.
    applicable: dto.applicable !== false,
    unknownCode: dto.unknownCode === true,
  };
}

export function mapRiskFactorsDtoToEntity(dto: RiskFactorsDto): RiskFactors {
  return {
    businessKey: dto.businessKey ?? "",
    scoringEventId: dto.scoringEventId ?? null,
    scoredAt: dto.scoredAt ?? null,
    scoringVersion: dto.scoringVersion ?? null,
    classification: dto.classification ?? null,
    score: dto.score ?? null,
    scoreMax: dto.scoreMax ?? null,
    assessedEntityCount: dto.assessedEntityCount ?? null,
    completenessPct: dto.completenessPct ?? null,
    hardOverrides: dto.hardOverrides ?? [],
    summary: dto.summary ?? null,
    decisiveFactors: (dto.decisiveFactors ?? []).map(mapFactor),
    factors: (dto.factors ?? []).map(mapFactor),
  };
}
